import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Header } from "../../components/Header/Header";
import useProjectsStore from "../../store/useProjectsStore";
import useIsKbReviewer from "../../utils/useIsKbReviewer";
import CreateProjectModal from "./Projects/CreateProjectModal";
import { PlusIcon } from "./Projects/MyProjectIcons";
import DeadlinesCard from "./Dashboard/DeadlinesCard";
import FunnelChart from "./Dashboard/FunnelChart";
import KpiCard, { KpiCardSkeleton } from "./Dashboard/KpiCard";
import RecentProjects from "./Dashboard/RecentProjects";
import useDashboardData from "./Dashboard/useDashboardData";
import {
  CHART_COLORS,
  KPI_COLORS,
  PERIODS,
  formatNumber,
  percentChip,
  periodByKey,
  readStoredPeriod,
  storePeriod,
} from "./Dashboard/dashboardFormat";
import "./Dashboard/dashboard.css";

const plural = (count, word) =>
  `${formatNumber(count)} ${word}${count === 1 ? "" : "s"}`;

const PeriodToggle = ({ value, onChange }) => (
  <div
    className="flex p-[3px] bg-[#F4F1F6] rounded-lg gap-0.5 text-[13px] font-medium"
    role="group"
    aria-label="Dashboard period"
  >
    {PERIODS.map((p) => (
      <button
        key={p.key}
        type="button"
        aria-pressed={value === p.key}
        onClick={() => onChange(p.key)}
        className={`px-3 py-1.5 rounded-md transition-colors ${
          value === p.key
            ? "bg-white text-[#18101C] shadow-[0_1px_2px_rgba(24,16,28,0.08)]"
            : "text-[#73607B] hover:text-[#18101C]"
        }`}
      >
        {p.label}
      </button>
    ))}
  </div>
);

const buildKpis = (kpis, period) => {
  if (!kpis?.projects) return [];
  const { previous, added } = periodByKey(period);
  const { projects, documents, proposals, kb_documents: kb } = kpis;
  return [
    {
      key: "projects",
      label: "Projects",
      value: formatNumber(projects.value),
      chip: percentChip(projects.change_percentage, projects.value),
      sub: previous,
      color: KPI_COLORS.projects,
      series: projects.series,
    },
    {
      key: "documents",
      label: "Documents uploaded",
      value: formatNumber(documents.value),
      chip: percentChip(documents.change_percentage, documents.value),
      sub: documents.per_project === null ? previous : `avg ${documents.per_project} per project`,
      color: KPI_COLORS.documents,
      series: documents.series,
    },
    {
      key: "proposals",
      label: "Proposals generated",
      value: formatNumber(proposals.value),
      chip: percentChip(proposals.change_percentage, proposals.value),
      sub: proposals.coverage === null ? previous : `${proposals.coverage}% of projects`,
      color: KPI_COLORS.proposals,
      series: proposals.series,
    },
    {
      key: "kb",
      label: "Knowledge Base docs",
      value: formatNumber(kb.value),
      chip: {
        text: `+${formatNumber(kb.added)}`,
        tone: kb.added > 0 ? "up" : "flat",
      },
      sub: added,
      color: KPI_COLORS.kb,
      series: kb.series,
    },
  ];
};

const RfpDashboard = () => {
  const [period, setPeriod] = useState(readStoredPeriod);
  const [createOpen, setCreateOpen] = useState(false);
  const { data, loading, error, reload } = useDashboardData(period);
  const projects = useProjectsStore((s) => s.projects);
  const projectsLoading = useProjectsStore((s) => s.loading);
  const fetchProjects = useProjectsStore((s) => s.fetchProjects);
  const isReviewer = useIsKbReviewer();

  // Layout effect so the store flips to loading before the first paint, as on the Projects page.
  useLayoutEffect(() => {
    fetchProjects();
    const onWorkspaceChange = () => fetchProjects();
    window.addEventListener("workspace-changed", onWorkspaceChange);
    return () =>
      window.removeEventListener("workspace-changed", onWorkspaceChange);
  }, [fetchProjects]);

  const changePeriod = (key) => {
    setPeriod(key);
    storePeriod(key);
  };

  // Deleting, archiving, leaving or creating a project changes the counts, so refresh the cards too.
  const projectCount = projects.length;
  const lastProjectCount = useRef(null);
  useEffect(() => {
    if (projectsLoading) return;
    if (lastProjectCount.current !== null && lastProjectCount.current !== projectCount) reload();
    lastProjectCount.current = projectCount;
  }, [projectCount, projectsLoading, reload]);

  const refreshAll = useCallback(() => {
    fetchProjects(true);
    reload();
  }, [fetchProjects, reload]);

  const kpiCards = useMemo(() => buildKpis(data?.kpis, period), [data, period]);
  const buckets = data?.funnel?.buckets || [];
  const projectsInPeriod = data?.kpis?.projects?.value ?? 0;
  const withProposal = data?.kpis?.proposals?.projects_with_proposal ?? 0;
  const coverage = data?.kpis?.proposals?.coverage;
  const chartSummary = !data
    ? "Loading this period"
    : projectsInPeriod === 0
      ? "No projects created this period"
      : `${formatNumber(withProposal)} of ${plural(projectsInPeriod, "project")} ${projectsInPeriod === 1 || withProposal === 1 ? "has" : "have"} a proposal`;

  const headerRight = (
    <div className="flex items-center gap-3">
      <PeriodToggle value={period} onChange={changePeriod} />
      {!isReviewer && (
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-5 py-2 bg-[#A04A97] rounded-md text-white text-[14px] font-semibold hover:bg-[#8a3f83] transition-colors"
        >
          <PlusIcon />
          Create Project
        </button>
      )}
    </div>
  );

  return (
    <div className="full-container max-w-full overflow-hidden !bg-[#F7F6F9] font-outfit">
      <Header name="Dashboard" icon="" headerRightContent={headerRight} />

      <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 md:px-7 py-4 flex flex-col gap-3">
        {error && !data ? (
          <div className="bg-white border border-black/[0.08] rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[14px] text-[#52525B]">
              The dashboard numbers could not be loaded.
            </span>
            <button
              type="button"
              onClick={reload}
              className="px-3.5 py-1.5 border border-[#C95EBE] text-[#A04A97] rounded-lg text-[13px] font-medium hover:bg-[#C95EBE]/5"
            >
              Try again
            </button>
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 transition-opacity ${loading && data ? "opacity-60" : ""}`}
          >
            {kpiCards.length
              ? kpiCards.map(({ key, ...card }, i) => (
                  <KpiCard key={key} {...card} delay={150 + i * 90} />
                ))
              : Array.from({ length: 4 }, (_, i) => (
                  <KpiCardSkeleton key={i} />
                ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4">
          <section className="bg-white border border-black/[0.08] rounded-xl shadow-[0_1px_3px_rgba(24,16,28,0.05)] px-5 pt-[18px] pb-3 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="m-0 text-[16px] font-medium text-[#18101C]">
                  Projects and proposals
                </h2>
                <p className="m-0 mt-0.5 text-[13px] text-[#73607B]">
                  {chartSummary}
                  {data && projectsInPeriod > 0 && coverage !== null && coverage !== undefined && (
                    <>
                      {" · "}
                      <b className="font-semibold text-[#18101C]">{coverage}% proposal coverage</b>
                    </>
                  )}
                </p>
              </div>
              <div className="flex gap-4 text-[12px] text-[#73607B] pt-1">
                <span className="flex items-center gap-1.5">
                  <span
                    className="w-2.5 h-2.5 rounded-sm"
                    style={{ background: CHART_COLORS.projects }}
                  />
                  Projects created
                </span>
                <span className="flex items-center gap-1.5">
                  <span
                    className="w-3.5 h-0.5 rounded-sm"
                    style={{ background: CHART_COLORS.proposals }}
                  />
                  Proposals generated
                </span>
              </div>
            </div>
            {data ? (
              <div
                className={
                  loading
                    ? "opacity-60 transition-opacity"
                    : "transition-opacity"
                }
              >
                <FunnelChart key={period} buckets={buckets} />
              </div>
            ) : (
              <div className="mt-2.5 h-[236px] rounded-lg bg-[#F7F4F8] animate-pulse" />
            )}
          </section>

          <DeadlinesCard projects={projects} loading={projectsLoading} />
        </div>

        <RecentProjects
          projects={projects}
          loading={projectsLoading}
        />
      </main>

      <CreateProjectModal
        isOpen={createOpen && !isReviewer}
        onClose={() => {
          setCreateOpen(false);
          refreshAll();
        }}
        onCreateProject={async () => {}}
      />
    </div>
  );
};

export default RfpDashboard;
